/**
 * SPDebugger — interactive stored-procedure debugger panel.
 *
 * Phase 1 (args): collect IN/INOUT argument values + dry-run choice.
 * Phase 2 (debug): instrumented run with step / continue / breakpoints, the
 * source with a current-line highlight + breakpoint gutter, and a live variable
 * watch — polling GET …/state. Backed by internal/api/spdebug.go.
 *
 * Props: connectionId, database, routine, source (CREATE/DROP text), onClose.
 *   sessionVarDefs? — [{ name:'@vFoo', label?, default? }] session variables to
 *     SET before the CALL and watch at every gate. Used when launching from
 *     Forms / Import (their RunProc binds @v… vars instead of SP parameters), so
 *     a parameterless proc sees real inputs. source may be '' in that case — the
 *     backend fetches the routine text itself and returns it for the debug view.
 */
import { createSignal, createMemo, onMount, onCleanup, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import Modal from './Modal.jsx';
import { toast } from '../lib/toast.js';
import {
  startSPDebug, spDebugState, spDebugStep, spDebugContinue,
  spDebugBreakpoints, spDebugStop, spDebugResultSets, spDebugExec, parseProcParams,
} from '../lib/spdebug.js';

export default function SPDebugger(props) {
  const [phase, setPhase] = createSignal('args'); // 'args' | 'debug'
  const [busy, setBusy] = createSignal(false);

  // ── args phase ──────────────────────────────────────────────────────────
  const params = createMemo(() => parseProcParams(props.source));
  const [args, setArgs] = createStore({});
  const [dryRun, setDryRun] = createSignal(true);

  // Session-variable presets (Forms / Import launch). Seeded from each def's
  // default; edited in the args phase; sent as sessionVars (and auto-watched).
  const sessionVarDefs = () => props.sessionVarDefs || [];
  const [sessVals, setSessVals] = createStore({});
  // Extra session vars the user adds by hand (e.g. a batch form's per-row
  // @v<Col>, which can't be enumerated from the definition). [{name, value}].
  const [customVars, setCustomVars] = createStore([]);

  // ── debug phase ─────────────────────────────────────────────────────────
  const [token, setToken] = createSignal('');
  const [source, setSource] = createSignal('');
  const [bpLines, setBpLines] = createSignal([]);     // breakpoint-able lines
  const [breakpoints, setBreakpoints] = createSignal(new Set());
  const [status, setStatus] = createSignal('running'); // running|paused|done|error
  const [curLine, setCurLine] = createSignal(0);
  const [vars, setVars] = createSignal([]);
  const [outParams, setOutParams] = createSignal(null);
  const [errMsg, setErrMsg] = createSignal('');
  // Result sets: rsMeta lists every bare-SELECT capture point (from start);
  // rsData holds the captured rows as they stream in (refetched when the
  // server's resultVer changes). They're merged into ordered tabs.
  const [rsMeta, setRsMeta] = createSignal([]);
  const [rsData, setRsData] = createSignal([]);
  const [rsTab, setRsTab] = createSignal(null);
  let poll, lastResultVer = 0;

  // ── ad-hoc SQL scratchpad (F12) ──────────────────────────────────────────
  const [sqlOpen, setSqlOpen] = createSignal(false);
  const [sqlText, setSqlText] = createSignal('');
  const [sqlBusy, setSqlBusy] = createSignal(false);
  const [sqlResult, setSqlResult] = createSignal(null); // { columns, rows, message, error, truncated }

  const sourceLines = createMemo(() => source().split('\n'));
  const bpSet = createMemo(() => new Set(bpLines()));

  // Split the watch panel: routine locals/params (plain names) vs watched
  // session variables (@-prefixed — what Forms/Import bind around the CALL).
  const localVars = createMemo(() => (vars() || []).filter((v) => !String(v.name).startsWith('@')));
  const sessVars = createMemo(() => (vars() || []).filter((v) => String(v.name).startsWith('@')));

  // One tab per capture point, in source order; merge any captured rows.
  const resultSets = createMemo(() => {
    const byIdx = new Map((rsData() || []).map((d) => [d.idx, d]));
    return (rsMeta() || []).map((m) => byIdx.get(m.idx) || { idx: m.idx, line: m.line, columns: null, rows: null });
  });
  const activeRs = createMemo(() => resultSets().find((r) => r.idx === rsTab()) || resultSets()[0]);

  const start = async () => {
    setBusy(true);
    try {
      const sessionVars = {};
      const watch = [];
      for (const d of sessionVarDefs()) { sessionVars[d.name] = sessVals[d.name] ?? ''; watch.push(d.name); }
      for (const c of customVars) {
        const name = (c.name || '').trim();
        if (name) { sessionVars[name] = c.value ?? ''; watch.push(name); }
      }
      const body = {
        connectionId: props.connectionId, database: props.database, routine: props.routine,
        args: { ...args }, mode: 'step', dryRun: dryRun(), breakpoints: [],
        sessionVars, watchVars: watch,
      };
      const r = await startSPDebug(body);
      setToken(r.token);
      setSource(r.source || '');
      setBpLines(r.lines || []);
      setRsMeta(r.resultSets || []);
      if ((r.resultSets || []).length) setRsTab(r.resultSets[0].idx);
      setPhase('debug');
      startPolling();
    } catch (e) {
      toast.error('Start failed: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const fetchResults = async () => {
    if (!token()) return;
    try {
      const rr = await spDebugResultSets(token());
      setRsData(rr.resultSets || []);
      if (rsTab() === null && (rr.resultSets || []).length) setRsTab(rr.resultSets[0].idx);
    } catch { /* transient */ }
  };

  const startPolling = () => {
    const tick = async () => {
      if (!token()) return;
      try {
        const s = await spDebugState(token());
        setStatus(s.status);
        if ((s.resultVer || 0) !== lastResultVer) { lastResultVer = s.resultVer || 0; fetchResults(); }
        if (s.status === 'paused') {
          setCurLine(s.line || 0);
          setVars(s.variables || []);
        } else if (s.status === 'done') {
          setCurLine(0); setVars([]); setOutParams(s.outParams || {});
          await fetchResults(); // ensure the final result set landed
          stopPolling();
        } else if (s.status === 'error') {
          setErrMsg(s.error || 'error'); await fetchResults(); stopPolling();
        }
      } catch { /* transient */ }
    };
    tick();
    poll = setInterval(tick, 300);
  };
  const stopPolling = () => { if (poll) { clearInterval(poll); poll = null; } };

  const step = async () => { try { await spDebugStep(token()); setStatus('running'); } catch (e) { toast.error(e?.message || e); } };
  const cont = async () => { try { await spDebugContinue(token()); setStatus('running'); } catch (e) { toast.error(e?.message || e); } };
  const stop = async () => {
    stopPolling();
    if (token()) { try { await spDebugStop(token()); } catch { /* ignore */ } }
    props.onClose?.();
  };

  const toggleBreakpoint = async (line) => {
    if (!bpSet().has(line)) return; // only breakpoint-able lines
    const next = new Set(breakpoints());
    if (next.has(line)) next.delete(line); else next.add(line);
    setBreakpoints(next);
    try { await spDebugBreakpoints(token(), [...next]); } catch (e) { toast.error(e?.message || e); }
  };

  const openSql = () => { setSqlResult(null); setSqlOpen(true); };
  const runSql = async () => {
    if (sqlBusy() || !sqlText().trim()) return;
    setSqlBusy(true);
    try {
      const r = await spDebugExec(token(), sqlText());
      setSqlResult(r);
    } catch (e) {
      setSqlResult({ error: e?.message || String(e) });
    } finally {
      setSqlBusy(false);
    }
  };

  // Keyboard shortcuts. F12 opens the SQL scratchpad anytime while debugging;
  // when it's open, Esc closes it (not the debugger) and Ctrl/Cmd+Enter runs.
  // Otherwise: Esc stops, F8 steps, F9 continues (paused only). Esc cancels args.
  const onKey = (e) => {
    if (phase() === 'args') {
      if (e.key === 'Escape') { e.preventDefault(); props.onClose?.(); }
      return;
    }
    if (phase() !== 'debug') return;
    if (sqlOpen()) {
      if (e.key === 'Escape') { e.preventDefault(); setSqlOpen(false); }
      else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); runSql(); }
      return; // swallow stepping shortcuts while the scratchpad is up
    }
    if (e.key === 'F12') { e.preventDefault(); openSql(); return; }
    if (e.key === 'Escape') { e.preventDefault(); stop(); return; }
    if (status() !== 'paused') return;
    if (e.key === 'F8') { e.preventDefault(); step(); }
    else if (e.key === 'F9') { e.preventDefault(); cont(); }
  };
  onMount(() => {
    for (const d of sessionVarDefs()) {
      if (sessVals[d.name] === undefined) setSessVals(d.name, d.default ?? '');
    }
    document.addEventListener('keydown', onKey);
  });
  onCleanup(() => {
    document.removeEventListener('keydown', onKey);
    stopPolling();
    if (token()) spDebugStop(token()).catch(() => {});
  });

  const statusLabel = () => ({
    running: 'running…', paused: 'paused', done: 'finished', error: 'error',
  }[status()] || status());

  return (
    <>
    <Modal open={true} onClose={stop} closeOnEscape={false} title={`Debug procedure ${props.routine}`} wide>
      <Show when={phase() === 'args'}>
        <p class="muted" style="font-size:13px;margin:0 0 10px;">
          Step through <code class="mono">{props.database}.{props.routine}</code>. Provide the input
          parameters, then start. Dry-run wraps the call in a transaction and rolls it back when you stop.
        </p>
        <For each={params().filter((p) => p.mode !== 'OUT')}>
          {(p) => (
            <div class="settings-row">
              <label>{p.name} <span class="muted mono" style="font-size:11px;">{p.mode} {p.type}</span></label>
              <input class="input" value={args[p.name] ?? ''} onInput={(e) => setArgs(p.name, e.currentTarget.value)} />
            </div>
          )}
        </For>
        <Show when={params().some((p) => p.mode === 'OUT')}>
          <p class="muted" style="font-size:12px;">OUT parameters are read back when the routine finishes.</p>
        </Show>

        <Show when={sessionVarDefs().length > 0}>
          <div class="spdbg-side-title" style="margin:10px 0 4px;">Session variables</div>
          <p class="muted" style="font-size:12px;margin:0 0 8px;">
            These are <code class="mono">SET</code> on the run session <em>before</em> the call — the same
            <code class="mono"> @v…</code> variables this form/import binds — so the procedure sees real inputs.
            They're also watched as it runs.
          </p>
          <For each={sessionVarDefs()}>
            {(d) => (
              <div class="settings-row">
                <label>{d.label || d.name} <span class="muted mono" style="font-size:11px;">{d.name}</span></label>
                <input class="input mono" value={sessVals[d.name] ?? ''} onInput={(e) => setSessVals(d.name, e.currentTarget.value)} />
              </div>
            )}
          </For>
          <For each={customVars}>
            {(c, i) => (
              <div class="settings-row">
                <label>
                  <input class="input mono" style="width:150px;" placeholder="@vName" value={c.name}
                         onInput={(e) => setCustomVars(i(), 'name', e.currentTarget.value)} />
                </label>
                <span style="display:flex;gap:6px;flex:1;">
                  <input class="input mono" style="flex:1;" placeholder="value" value={c.value}
                         onInput={(e) => setCustomVars(i(), 'value', e.currentTarget.value)} />
                  <button class="btn btn-sm btn-ghost" title="Remove" onClick={() => setCustomVars((p) => p.filter((_, j) => j !== i()))}>✕</button>
                </span>
              </div>
            )}
          </For>
          <button class="btn btn-sm btn-ghost" style="margin-top:2px;"
                  onClick={() => setCustomVars(customVars.length, { name: '', value: '' })}>
            ＋ Add session variable
          </button>
          <p class="muted" style="font-size:11px;margin:6px 0 0;">
            Add any other <code class="mono">@v…</code> the procedure reads — e.g. a batch form binds each row’s
            columns as <code class="mono">@v&lt;Column&gt;</code>. Enter one representative row’s values to debug a single call.
          </p>
        </Show>
        <div class="settings-row">
          <label>Dry run</label>
          <label class="checkbox"><input type="checkbox" checked={dryRun()} onChange={(e) => setDryRun(e.currentTarget.checked)} />
            <span>roll back all writes on stop (safe against production)</span></label>
        </div>
        <Show when={!dryRun()}>
          <p class="run-err-text" style="font-size:12px;">⚠ Not a dry run — the procedure's writes will be committed for real.</p>
        </Show>
        <p class="muted" style="font-size:11px;margin:6px 0 0;">
          Result-returning <code class="mono">SELECT</code>s are captured into tables so you can view them. Capturing
          uses <code class="mono">CREATE TABLE</code>, which auto-commits — so on a procedure that writes <em>then</em> selects,
          a dry run can only roll back writes made after its last captured SELECT.
        </p>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px;">
          <button class="btn btn-ghost" onClick={() => props.onClose?.()}>Cancel</button>
          <button class="btn btn-primary" disabled={busy()} onClick={start}>🐞 Start debugging</button>
        </div>
      </Show>

      <Show when={phase() === 'debug'}>
        <div class="spdbg-toolbar">
          <span class={'spdbg-status spdbg-' + status()}>{statusLabel()}</span>
          <div class="spacer" />
          <button class="btn btn-sm" disabled={status() !== 'paused'} onClick={step} title="Run the next statement (F8)">⤼ Step <span class="spdbg-key">F8</span></button>
          <button class="btn btn-sm" disabled={status() !== 'paused'} onClick={cont} title="Run to the next breakpoint (F9)">▶ Continue <span class="spdbg-key">F9</span></button>
          <button class="btn btn-sm" onClick={openSql} title="Run an arbitrary SQL statement against the session (F12)">⌨ Run SQL <span class="spdbg-key">F12</span></button>
          <button class="btn btn-sm btn-danger" onClick={stop} title="Stop debugging — rolls back a dry run (Esc)">⏹ Stop</button>
        </div>

        <div class="spdbg-body">
          <div class="spdbg-source">
            <For each={sourceLines()}>
              {(text, i) => {
                const ln = () => i() + 1;
                return (
                  <div class="spdbg-line" classList={{ 'spdbg-current': ln() === curLine() }}>
                    <span class="spdbg-gutter" classList={{ 'spdbg-bp-able': bpSet().has(ln()) }}
                          onClick={() => toggleBreakpoint(ln())}
                          title={bpSet().has(ln()) ? 'Toggle breakpoint' : ''}>
                      <span class="spdbg-bp" classList={{ on: breakpoints().has(ln()) }}>●</span>
                      <span class="spdbg-lno">{ln()}</span>
                    </span>
                    <span class="spdbg-code">{text || ' '}</span>
                  </div>
                );
              }}
            </For>
          </div>

          <div class="spdbg-side">
            <div class="spdbg-side-title">Variables</div>
            <Show when={status() === 'paused'} fallback={<div class="muted" style="font-size:12px;padding:6px;">{status() === 'running' ? 'running…' : '—'}</div>}>
              <table class="spdbg-vars">
                <tbody>
                  <For each={localVars()}>
                    {(v) => (
                      <tr><td class="mono spdbg-vname">{v.name}</td>
                          <td class="mono">{v.val === null ? <span class="muted">NULL</span> : v.val}</td></tr>
                    )}
                  </For>
                  <Show when={sessVars().length > 0}>
                    <tr><td class="spdbg-vgroup muted" colspan="2">session</td></tr>
                    <For each={sessVars()}>
                      {(v) => (
                        <tr><td class="mono spdbg-vname">{v.name}</td>
                            <td class="mono">{v.val === null ? <span class="muted">NULL</span> : v.val}</td></tr>
                      )}
                    </For>
                  </Show>
                  <Show when={(vars() || []).length === 0}><tr><td class="muted" colspan="2">no variables in scope</td></tr></Show>
                </tbody>
              </table>
            </Show>
          </div>
        </div>

        <Show when={resultSets().length > 0}>
          <div class="spdbg-rs">
            <div class="spdbg-rs-tabs">
              <span class="spdbg-rs-label">Result set</span>
              <For each={resultSets()}>
                {(rs) => (
                  <button class="spdbg-rs-tab" classList={{ active: rs.idx === rsTab() }} onClick={() => setRsTab(rs.idx)}>
                    line {rs.line}
                    <Show when={rs.rows !== null}><span class="muted"> · {rs.rowCount}{rs.truncated ? '+' : ''}</span></Show>
                  </button>
                )}
              </For>
            </div>
            <Show when={activeRs()} fallback={<div class="muted" style="padding:8px;font-size:12px;">—</div>}>
              <div class="spdbg-rs-grid">
                <Show when={activeRs().error}>
                  <div class="run-err-text" style="padding:8px;font-size:12px;">{activeRs().error}</div>
                </Show>
                <Show when={!activeRs().error && activeRs().rows === null}>
                  <div class="muted" style="padding:8px;font-size:12px;">not captured yet — runs when execution reaches line {activeRs().line}</div>
                </Show>
                <Show when={!activeRs().error && activeRs().rows !== null}>
                  <Show when={(activeRs().rows || []).length > 0} fallback={<div class="muted" style="padding:8px;font-size:12px;">0 rows</div>}>
                    <table class="spdbg-rs-table">
                      <thead>
                        <tr><For each={activeRs().columns || []}>{(c) => <th>{c}</th>}</For></tr>
                      </thead>
                      <tbody>
                        <For each={activeRs().rows || []}>
                          {(row) => (
                            <tr><For each={row}>{(cell) => <td class="mono">{cell === null ? <span class="muted">NULL</span> : cell}</td>}</For></tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </Show>
                  <Show when={activeRs().truncated}>
                    <div class="muted" style="padding:4px 8px;font-size:11px;">showing first {(activeRs().rows || []).length} rows (truncated)</div>
                  </Show>
                </Show>
              </div>
            </Show>
          </div>
        </Show>

        <Show when={status() === 'done'}>
          <div class="spdbg-result">
            <strong>Finished.</strong>
            <Show when={outParams() && Object.keys(outParams()).length > 0}>
              <span> OUT: </span>
              <For each={Object.entries(outParams())}>
                {([k, v]) => <span class="mono" style="margin-right:10px;">{k} = {v === null ? 'NULL' : v}</span>}
              </For>
            </Show>
          </div>
        </Show>
        <Show when={status() === 'error'}>
          <div class="spdbg-result run-err-text">Error: {errMsg()}</div>
        </Show>
      </Show>
    </Modal>

    <Show when={sqlOpen()}>
      <Modal open={true} onClose={() => setSqlOpen(false)} closeOnBackdrop={false} closeOnEscape={false}
             title="Run SQL statement" wide>
        <p class="muted" style="font-size:12px;margin:0 0 8px;">
          Runs on a <strong>separate</strong> session with <code class="mono">READ UNCOMMITTED</code>, so it sees the
          procedure's <em>uncommitted</em> writes so far — but not its local variables (use the watch panel).
          Writes here run in their own transaction, independent of the debugged procedure.
        </p>
        <textarea class="input mono" rows="5" placeholder="SELECT * FROM …"
                  style="width:100%;resize:vertical;font-size:13px;"
                  value={sqlText()} onInput={(e) => setSqlText(e.currentTarget.value)} />
        <div style="display:flex;align-items:center;gap:8px;margin-top:8px;">
          <button class="btn btn-primary btn-sm" disabled={sqlBusy() || !sqlText().trim()} onClick={runSql}>
            {sqlBusy() ? 'Running…' : 'Run'} <span class="spdbg-key">Ctrl↵</span>
          </button>
          <button class="btn btn-ghost btn-sm" onClick={() => setSqlOpen(false)}>Close <span class="spdbg-key">Esc</span></button>
        </div>

        <Show when={sqlResult()}>
          <div class="spdbg-rs" style="margin-top:10px;">
            <Show when={sqlResult().error}>
              <div class="run-err-text" style="padding:8px;font-size:12px;">{sqlResult().error}</div>
            </Show>
            <Show when={!sqlResult().error && sqlResult().columns}>
              <div class="spdbg-rs-grid">
                <Show when={(sqlResult().rows || []).length > 0} fallback={<div class="muted" style="padding:8px;font-size:12px;">0 rows</div>}>
                  <table class="spdbg-rs-table">
                    <thead><tr><For each={sqlResult().columns || []}>{(c) => <th>{c}</th>}</For></tr></thead>
                    <tbody>
                      <For each={sqlResult().rows || []}>
                        {(row) => (
                          <tr><For each={row}>{(cell) => <td class="mono">{cell === null ? <span class="muted">NULL</span> : cell}</td>}</For></tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </Show>
                <Show when={sqlResult().truncated}>
                  <div class="muted" style="padding:4px 8px;font-size:11px;">showing first {(sqlResult().rows || []).length} rows (truncated)</div>
                </Show>
              </div>
            </Show>
            <Show when={!sqlResult().error && !sqlResult().columns}>
              <div style="padding:8px;font-size:12px;">{sqlResult().message || 'OK'}</div>
            </Show>
          </div>
        </Show>
      </Modal>
    </Show>
    </>
  );
}
